/**
 * jodia-predictive-search.js
 * Replaces Minion's native predictive search with Typesense-powered results.
 *
 * Hooks into the existing <predictive-search> Web Component structure in
 * snippets/header-search.liquid — no changes to the form HTML needed.
 * Falls back gracefully to Shopify native search on submit if Typesense
 * is unavailable.
 *
 * Config is read from window.JodiaSearch set by header-search.liquid:
 *   window.JodiaSearch = {
 *     apiKey: '...',   // scoped search-only key
 *     host: 'search.jodiabaazar.com',
 *     enabled: true
 *   };
 */

import {
  createTypesenseClient,
  searchProducts,
  formatFromPrice,
  formatPKR,
  escapeHtml
} from './jodia-search-core.js';

(function() {
  'use strict';

  // ── Config guard ───────────────────────────────────────────────────────────
  const config = window.JodiaSearch;
  if (!config || !config.enabled || !config.apiKey || !config.host) {
    console.log('[jodia-search] Not configured or disabled — native search active');
    return;
  }

  const SHOW_VENDOR = !!config.showVendor;
  const SHOW_PRICE  = config.showPrice !== false; // default true

  let typesenseClient;
  try {
    typesenseClient = createTypesenseClient({
      apiKey: config.apiKey,
      host: config.host,
      port: '443',
      protocol: 'https'
    });
  } catch (e) {
    console.error('[jodia-search] Client init failed:', e.message);
    return;
  }

  // ── Constants ──────────────────────────────────────────────────────────────
  const DEBOUNCE_MS    = 250;
  const MIN_QUERY_LEN  = 2;
  const MAX_RESULTS    = 8;
  const SEARCH_PAGE    = '/pages/search';

  // ── State ──────────────────────────────────────────────────────────────────
  let debounceTimer = null;
  let lastQuery     = '';
  let searchSeq     = 0;   // bumped by every new search; lets us ignore slow, outdated responses

  // ── Find all search inputs (desktop + mobile) ──────────────────────────────
  function init() {
    const inputs = document.querySelectorAll(
      '#Search-In-Modal, #Search-In-Modal-mobile'
    );

    inputs.forEach(function(input) {
      const form      = input.closest('form');
      const dropdown  = input.closest('.field').querySelector('[data-predictive-search]');
      const psWrapper = input.closest('predictive-search');

      if (!dropdown) return;

      injectStyles();

      if (psWrapper) {
        psWrapper.setAttribute('data-jodia-search', 'active');
      }

      input.addEventListener('input', function() {
        const query = input.value.trim();
        clearTimeout(debounceTimer);

        if (query.length < MIN_QUERY_LEN) {
          lastQuery = '';      // so retyping the same term searches again
          searchSeq++;         // ignore any search still in flight
          hideDropdown(dropdown, input);
          return;
        }

        debounceTimer = setTimeout(function() {
          if (query !== lastQuery) {
            lastQuery = query;
            runSearch(query, dropdown, input, form);
          }
        }, DEBOUNCE_MS);
      });

      document.addEventListener('click', function(e) {
        if (!input.closest('.field').contains(e.target)) {
          hideDropdown(dropdown, input);
        }
      });

      input.addEventListener('keydown', function(e) {
        if (e.key === 'Escape') {
          hideDropdown(dropdown, input);
          input.blur();
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          focusResult(dropdown, 0);
        }
      });

      if (form) {
        form.addEventListener('submit', function(e) {
          const q = input.value.trim();
          if (q) {
            e.preventDefault();
            window.location.href = SEARCH_PAGE + '?q=' + encodeURIComponent(q);
          }
        });
      }
    });
  }

  // ── Search execution ───────────────────────────────────────────────────────
  async function runSearch(query, dropdown, input, form) {
    const seq = ++searchSeq;   // a newer search (or a cleared box) makes this one outdated
    showLoading(dropdown, input);

    try {
      // No facetBy here: the dropdown never shows facets, so computing them is wasted work.
      const result = await searchProducts(typesenseClient, query, {
        perPage: MAX_RESULTS,
        filterBy: 'status:=ACTIVE && inventory_quantity:>0'
      });

      if (seq !== searchSeq) return;
      renderResults(result, query, dropdown, input);
    } catch (err) {
      if (seq !== searchSeq) return;
      console.error('[jodia-search] Search error:', err.message);
      hideDropdown(dropdown, input);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  function renderResults(result, query, dropdown, input) {
    if (!result.products.length) {
      renderEmpty(query, dropdown, input);
      return;
    }

     // Deduplicate by normalized title to hide duplicate catalogue entries
    const seenTitles = new Set();
    result.products = result.products.filter(function(p) {
      const key = p.title.toLowerCase().replace(/\s+/g, ' ').trim();
      if (seenTitles.has(key)) return false;
      seenTitles.add(key);
      return true;
    }).slice(0, 6);

    const searchUrl = SEARCH_PAGE + '?q=' + encodeURIComponent(query);

    let html = '<div class="jodia-ps__results" role="listbox" id="predictive-search-results-list">';

    result.products.forEach(function(p, i) {
      const title  = p.titleHighlight
        ? p.titleHighlight.replace(/<mark>/g, '<mark class="jodia-ps__mark">')
        : escapeHtml(p.title);
      const handle = encodeURIComponent(p.handle || String(p.productId));
      const imgSrc = escapeHtml(p.imageUrl);
      const vendor = SHOW_VENDOR ? escapeHtml(p.vendorBrand) : '';
      const price  = formatFromPrice(p.fromPrice);

      html += '<a href="/products/' + handle + '"' +
              ' class="jodia-ps__item"' +
              ' role="option"' +
              ' tabindex="0"' +
              ' data-index="' + i + '">' +
        (imgSrc
          ? '<img class="jodia-ps__img" src="' + imgSrc + '" alt="' + escapeHtml(p.title) + '" loading="lazy" width="56" height="56">'
          : '<div class="jodia-ps__img jodia-ps__img--placeholder"></div>') +
        '<div class="jodia-ps__info">' +
          '<div class="jodia-ps__title">' + title + '</div>' +
          (vendor ? '<div class="jodia-ps__vendor">' + vendor + '</div>' : '') +
          (SHOW_PRICE
            ? '<div class="jodia-ps__price">' + price +
                (p.isOnSale ? ' <s class="jodia-ps__compare">' + formatPKR(p.compareAt) + '</s>' : '') +
              '</div>'
            : '') +
        '</div>' +
      '</a>';
    });

    html += '<a href="' + escapeHtml(searchUrl) + '" class="jodia-ps__see-all">' +
      'See all ' + result.found.toLocaleString() + ' results for &ldquo;' + escapeHtml(query) + '&rdquo; &rarr;' +
    '</a>';

    html += '</div>';

    dropdown.innerHTML = html;
    showDropdown(dropdown, input);

    dropdown.querySelectorAll('.jodia-ps__item').forEach(function(item, i) {
      item.addEventListener('keydown', function(e) {
        if (e.key === 'ArrowDown') { e.preventDefault(); focusResult(dropdown, i + 1); }
        if (e.key === 'ArrowUp')   { e.preventDefault(); i === 0 ? input.focus() : focusResult(dropdown, i - 1); }
        if (e.key === 'Escape')    { hideDropdown(dropdown, input); input.focus(); }
      });
    });
  }

  function renderEmpty(query, dropdown, input) {
    const searchUrl = SEARCH_PAGE + '?q=' + encodeURIComponent(query);
    dropdown.innerHTML =
      '<div class="jodia-ps__empty">' +
        '<p>No products found for &ldquo;' + escapeHtml(query) + '&rdquo;</p>' +
        '<a href="' + escapeHtml(searchUrl) + '" class="jodia-ps__see-all">Search all products &rarr;</a>' +
      '</div>';
    showDropdown(dropdown, input);
  }

  // ── Dropdown state ─────────────────────────────────────────────────────────
  function showLoading(dropdown, input) {
    dropdown.innerHTML =
      '<div class="jodia-ps__loading">' +
        '<svg class="jodia-ps__spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">' +
          '<circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>' +
        '</svg>' +
      '</div>';
    showDropdown(dropdown, input);
  }

  function showDropdown(dropdown, input) {
    dropdown.style.display = 'block';
    dropdown.removeAttribute('hidden');
    input.setAttribute('aria-expanded', 'true');
  }

  function hideDropdown(dropdown, input) {
    dropdown.style.display = 'none';
    dropdown.setAttribute('hidden', '');
    input.setAttribute('aria-expanded', 'false');
  }

  function focusResult(dropdown, index) {
    const items = dropdown.querySelectorAll('.jodia-ps__item');
    if (items[index]) items[index].focus();
  }

  // ── Styles ─────────────────────────────────────────────────────────────────
  function injectStyles() {
    if (document.getElementById('jodia-ps-styles')) return;
    const style = document.createElement('style');
    style.id = 'jodia-ps-styles';
    style.textContent = `
      [data-predictive-search] {
        position: absolute;
        top: calc(100% + 4px);
        left: 0;
        right: 0;
        background: var(--color-body-background, #fff);
        border: 1px solid var(--color-lines-borders, #e0e0e0);
        border-radius: var(--radius, 8px);
        box-shadow: 0 8px 32px rgba(0,0,0,.12);
        z-index: 9999;
        overflow: hidden;
        max-height: 480px;
        overflow-y: auto;
      }
      .jodia-ps__results {
        padding: 8px 0;
        max-height: 400px;
        overflow-y: auto;
      }
      .jodia-ps__item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 10px 16px;
        text-decoration: none;
        color: var(--color-base, #333);
        transition: background .15s;
        cursor: pointer;
        outline: none;
      }
      .jodia-ps__item:hover,
      .jodia-ps__item:focus {
        background: var(--color-secondary-background, #f5f5f5);
      }
      .jodia-ps__img {
        width: 56px;
        height: 56px;
        object-fit: cover;
        border-radius: calc(var(--radius, 8px) * .5);
        flex-shrink: 0;
        background: var(--color-secondary-background, #f5f5f5);
      }
      .jodia-ps__img--placeholder {
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .jodia-ps__info {
        flex: 1;
        min-width: 0;
      }
      .jodia-ps__title {
        font-size: .9rem;
        font-weight: 600;
        line-height: 1.3;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        color: var(--color-base, #333);
      }
      .jodia-ps__mark {
        background: transparent;
        color: var(--color-accent, #BD1E2E);
        font-weight: 700;
      }
      .jodia-ps__vendor {
        font-size: .75rem;
        color: #888;
        margin-top: 2px;
      }
      .jodia-ps__price {
        font-size: .85rem;
        font-weight: 700;
        color: var(--color-price, var(--color-accent, #BD1E2E));
        margin-top: 4px;
      }
      .jodia-ps__compare {
        font-size: .75rem;
        color: #aaa;
        font-weight: 400;
        margin-left: 6px;
      }
      .jodia-ps__see-all {
        display: block;
        padding: 12px 16px;
        font-size: .85rem;
        color: var(--color-accent, #BD1E2E);
        text-decoration: none;
        border-top: 1px solid var(--color-lines-borders, #e0e0e0);
        font-weight: 600;
        transition: background .15s;
      }
      .jodia-ps__see-all:hover { background: var(--color-secondary-background, #f5f5f5); }
      .jodia-ps__empty {
        padding: 24px 16px;
        text-align: center;
        color: #888;
        font-size: .9rem;
      }
      .jodia-ps__empty p { margin: 0 0 12px; }
      .jodia-ps__loading {
        padding: 24px;
        display: flex;
        justify-content: center;
      }
      .jodia-ps__spinner {
        width: 28px;
        height: 28px;
        animation: jodia-spin 1s linear infinite;
      }
      .jodia-ps__spinner .path {
        stroke: var(--color-accent, #BD1E2E);
        stroke-linecap: round;
      }
      @keyframes jodia-spin {
        0%   { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
  }

  // ── Boot ───────────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();